/**
 * Stripe Payment Service
 * 
 * Handles Stripe subscription management including checkout, portal, and subscription status.
 */

import { getAuth } from 'firebase/auth';
import { env } from '../utils/env';
import { loggedFetch } from '../utils/apiLogger';
import { getFirebaseIdToken } from '../utils/firebaseToken';
import { firebaseApp } from '../services/firebase/firebase.config';

const auth = getAuth(firebaseApp);

// Single-flight + TTL cache for getSubscription.
// Prevents concurrent callers during boot from each firing a backend call.
// Cache is keyed by uid — result never leaks across users.
let _subInFlight: Promise<GetSubscriptionResponse> | null = null;
let _subCache: { result: GetSubscriptionResponse; at: number; uid: string } | null = null;
const SUB_TTL_MS = 30_000;

export function invalidateSubscriptionCache(): void {
  _subInFlight = null;
  _subCache = null;
}

/**
 * Get current user's email
 */
function getUserEmail(): string {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('User email not available');
  }
  return user.email;
}

/**
 * Create Checkout Session Response
 */
export interface CreateCheckoutSessionResponse {
  id?: string;
  url?: string | null;
  message?: string;
  allowTrial?: boolean;
}

/**
 * Create a Stripe Checkout Session for subscription
 */
export async function createCheckoutSession(
  email: string,
  currency: 'usd' | 'eur' | 'gbp',
  interval: 'monthly' | 'annual',
  successUrl?: string,
  cancelUrl?: string,
  discountCode?: string,
): Promise<CreateCheckoutSessionResponse> {
  console.log('[Stripe] createCheckoutSession: Starting', {
    email,
    currency,
    interval,
    timestamp: new Date().toISOString(),
  });

  try {
    const token = await getFirebaseIdToken();
    console.log('[Stripe] Firebase token obtained');

    const url = `${env.apiBaseUrl}/api/stripe/create-checkout-session`;
    console.log('[Stripe] Calling backend', {
      url: url.replace(env.apiBaseUrl, '[API_BASE_URL]'),
    });

    const requestBody: {
      email: string;
      currency: string;
      interval: string;
      successUrl?: string;
      cancelUrl?: string;
      discountCode?: string;
    } = {
      email,
      currency,
      interval,
    };

    if (successUrl) {
      requestBody.successUrl = successUrl;
    }
    if (cancelUrl) {
      requestBody.cancelUrl = cancelUrl;
    }
    if (discountCode) {
      requestBody.discountCode = discountCode;
    }
    const response = await loggedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[Stripe] createCheckoutSession: Failed', {
        status: response.status,
        error,
      });
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const result = await response.json() as CreateCheckoutSessionResponse;
    console.log('[Stripe] createCheckoutSession: Success', {
      hasId: !!result.id,
      hasUrl: !!result.url,
      hasMessage: !!result.message,
    });
    return result;
  } catch (error) {
    console.error('[Stripe] createCheckoutSession: Error', {
      error: error instanceof Error ? error.message : error,
    });
    throw error;
  }
}

/**
 * Create Customer Portal Response
 */
export interface CreateCustomerPortalResponse {
  url: string;
}

/**
 * Create a Stripe Customer Portal session for managing subscription
 */
export async function createCustomerPortal(
  customerId?: string,
): Promise<CreateCustomerPortalResponse> {
  console.log('[Stripe] createCustomerPortal: Starting', {
    customerId,
    timestamp: new Date().toISOString(),
  });

  try {
    const token = await getFirebaseIdToken();
    console.log('[Stripe] Firebase token obtained');

    const url = `${env.apiBaseUrl}/api/stripe/portal`;
    console.log('[Stripe] Calling backend', {
      url: url.replace(env.apiBaseUrl, '[API_BASE_URL]'),
    });

    const requestBody: { customerId?: string } = {};
    if (customerId) {
      requestBody.customerId = customerId;
    }

    const response = await loggedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[Stripe] createCustomerPortal: Failed', {
        status: response.status,
        error,
      });
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const result = await response.json() as CreateCustomerPortalResponse;
    console.log('[Stripe] createCustomerPortal: Success', {
      hasUrl: !!result.url,
    });
    return result;
  } catch (error) {
    console.error('[Stripe] createCustomerPortal: Error', {
      error: error instanceof Error ? error.message : error,
    });
    throw error;
  }
}

/**
 * Subscription Data
 */
export interface SubscriptionData {
  userId: string;
  isPremium: boolean;
  plan: string;
  status: string;
  periodEnd: number;
  cancelAtPeriodEnd: boolean;
  currency: string;
  betaType: string | null;
  customerId: string;
  subscriptionId: string;
  trialStart: number | null;
  trialEnd: number | null;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  premiumPreviewExpiresAt?: string | null;
  premiumPreviewUsed?: boolean;
  previewActive?: boolean;
  entitlementReason?: string;
}

/**
 * Get Subscription Response
 */
export interface GetSubscriptionResponse {
  success: boolean;
  data: SubscriptionData | null;
  message: string;
}

/**
 * Get current subscription status and details
 */
export async function getSubscription(
  sessionId?: string,
): Promise<GetSubscriptionResponse> {
  const uid = auth.currentUser?.uid ?? null;
  const isBypass = !!sessionId; // post-checkout: always fresh, skip all gates

  console.log('[Stripe] getSubscription: Starting', {
    sessionId,
    bypass: isBypass,
    uid,
    timestamp: new Date().toISOString(),
  });

  if (!isBypass) {
    // TTL cache hit — same user, within window
    if (_subCache && _subCache.uid === uid && Date.now() - _subCache.at < SUB_TTL_MS) {
      console.log('[Stripe] getSubscription: Cache hit', { uid, ageMs: Date.now() - _subCache.at });
      return _subCache.result;
    }

    // In-flight — await existing promise, no duplicate network call
    if (_subInFlight) {
      console.log('[Stripe] getSubscription: Awaiting in-flight request');
      return _subInFlight;
    }
  }

  const p = (async (): Promise<GetSubscriptionResponse> => {
    try {
      const token = await getFirebaseIdToken();
      console.log('[Stripe] Firebase token obtained');

      const url = `${env.apiBaseUrl}/api/stripe/subscription`;
      console.log('[Stripe] Calling backend', { url: url.replace(env.apiBaseUrl, '[API_BASE_URL]') });

      const requestBody: { sessionId?: string } = {};
      if (sessionId) requestBody.sessionId = sessionId;

      const response = await loggedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[Stripe] getSubscription: Failed', { status: response.status, error });
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      const result = await response.json() as GetSubscriptionResponse;

      if (result.data) {
        console.log('[Stripe] getSubscription: Success', {
          isPremium: result.data.isPremium,
          status: result.data.status,
          plan: result.data.plan,
        });
      } else {
        console.log('[Stripe] getSubscription: No subscription found');
      }

      // Only cache on success, keyed by uid — never cache failures
      if (!isBypass && uid) {
        _subCache = { result, at: Date.now(), uid };
      }

      return result;
    } catch (error) {
      console.error('[Stripe] getSubscription: Error', {
        error: error instanceof Error ? error.message : error,
      });
      throw error; // never swallow — let callers handle gracefully
    } finally {
      // Always clear inFlight so a thrown error can never leave it stuck
      _subInFlight = null;
    }
  })();

  if (!isBypass) {
    _subInFlight = p;
  }

  return p;
}

/**
 * Prices Response
 */
export interface PricesResponse {
  usd: {
    monthly: string;
    annual: string;
  };
  eur: {
    monthly: string;
    annual: string;
  };
  gbp: {
    monthly: string;
    annual: string;
  };
}

/**
 * Get available subscription prices for all currencies
 */
export async function getPrices(): Promise<PricesResponse> {
  console.log('[Stripe] getPrices: Starting', {
    timestamp: new Date().toISOString(),
  });

  try {
    const token = await getFirebaseIdToken();
    console.log('[Stripe] Firebase token obtained');

    const url = `${env.apiBaseUrl}/api/stripe/prices`;
    console.log('[Stripe] Calling backend', {
      url: url.replace(env.apiBaseUrl, '[API_BASE_URL]'),
    });

    const response = await loggedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[Stripe] getPrices: Failed', {
        status: response.status,
        error,
      });
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const result = await response.json() as PricesResponse;
    console.log('[Stripe] getPrices: Success', {
      hasUsd: !!result.usd,
      hasEur: !!result.eur,
      hasGbp: !!result.gbp,
    });
    return result;
  } catch (error) {
    console.error('[Stripe] getPrices: Error', {
      error: error instanceof Error ? error.message : error,
    });
    throw error;
  }
}

// Export helper function for getting user email
export { getUserEmail };
